import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOpenPos, type OpenPo, type OpenPosResponse } from '@/api/onderaannemers';
import { projectDashboardPath } from '@/utils/projectDashboardLink';
import { PoEditPanel } from './poedit/PoEditPanel';

// Read-only overview of the current OPEN rolling PO per onderaannemer, read straight
// off Odoo's draft `[LOPEND]` POs. No editing here — that's Fase 2.

const euro = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });
const m2fmt = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 });

function formatM2(n: number): string {
  return `${m2fmt.format(n)} m²`;
}

// '2026-07-02 10:43:53' → '2 jul 2026'.
const SHORT_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
function formatWriteDate(v: string | null): string {
  if (!v) return '—';
  const [y, m, d] = v.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return v;
  return `${d} ${SHORT_MONTHS[m - 1]} ${y}`;
}

function regelWord(n: number): string {
  return `${n} ${n === 1 ? 'regel' : 'regels'}`;
}

function PoCard({
  po,
  editing,
  onEnterEdit,
  onExitEdit,
}: {
  po: OpenPo;
  editing: boolean;
  onEnterEdit: () => void;
  onExitEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // In edit mode the whole card is the editor; the read-only summary/detail is hidden.
  if (editing) {
    return (
      <div className="bg-white border border-td-blue/40 rounded-xl overflow-hidden ring-1 ring-td-blue/20">
        <PoEditPanel poId={po.poId} onDone={onExitEdit} />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 text-left flex-1 min-w-0"
          aria-expanded={open}
        >
          <span className={`text-td-muted transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden>▶</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-td-navy truncate">{po.vendorName}</span>
              <span className="text-xs font-mono text-td-muted bg-gray-100 px-1.5 py-0.5 rounded shrink-0">{po.poName}</span>
              {po.teBepalenCount > 0 && (
                <span
                  className="text-xs font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded shrink-0"
                  title="Collectoren op deze PO zonder prijs — nog in te vullen"
                >
                  {po.teBepalenCount}× prijs te bepalen
                </span>
              )}
            </div>
            <div className="text-xs text-td-muted mt-0.5">
              {regelWord(po.lineCount)} · {formatM2(po.totalM2)} · laatst gewijzigd {formatWriteDate(po.writeDate)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-bold text-td-navy">{euro.format(po.amountUntaxed)}</div>
            <div className="text-[11px] text-td-muted">excl. btw</div>
          </div>
        </button>
        <button
          type="button"
          onClick={onEnterEdit}
          className="shrink-0 text-sm font-semibold text-td-blue border border-td-blue/40 rounded-lg px-3 py-1.5 hover:bg-td-blue/5 transition-colors"
        >
          Bewerken
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-2 sm:px-4 py-3 space-y-4">
          {po.sections.length === 0 ? (
            <p className="text-sm text-td-muted italic px-2">Deze PO heeft nog geen regels.</p>
          ) : (
            po.sections.map((section, si) => (
              <div key={si}>
                {section.header && (
                  section.projectId != null ? (
                    <button
                      type="button"
                      onClick={() => navigate(projectDashboardPath(section.projectId!, section.collectorId))}
                      title="Open het dashboard van dit project"
                      className="w-full text-left text-xs font-semibold text-td-navy bg-td-blue/5 hover:bg-td-blue/10 hover:text-td-blue px-2 py-1 rounded mb-1 transition-colors cursor-pointer"
                    >
                      {section.header}
                    </button>
                  ) : (
                    <div className="text-xs font-semibold text-td-navy bg-td-blue/5 px-2 py-1 rounded mb-1">
                      {section.header}
                    </div>
                  )
                )}
                <table className="w-full text-sm">
                  <tbody>
                    {section.lines.map((line) => (
                      <tr
                        key={line.id}
                        className={
                          line.kind === 'note'
                            ? 'text-td-muted italic'
                            : line.teBepalen
                              ? 'border-b border-red-100 last:border-0 bg-red-50/60'
                              : 'border-b border-gray-50 last:border-0'
                        }
                      >
                        <td className={`py-1 pr-2 align-top ${line.teBepalen ? 'text-red-800 font-medium' : ''}`}>{line.label}</td>
                        {line.kind === 'product' ? (
                          <>
                            <td className="py-1 px-2 text-right whitespace-nowrap tabular-nums text-td-muted align-top">
                              {line.m2 != null ? m2fmt.format(line.m2) : ''}
                            </td>
                            <td className="py-1 px-2 text-right whitespace-nowrap tabular-nums align-top">
                              {line.teBepalen ? (
                                <span className="font-semibold text-red-600">te bepalen</span>
                              ) : (
                                <span className="text-td-muted">{line.rate != null ? euro.format(line.rate) : ''}</span>
                              )}
                            </td>
                            <td className={`py-1 pl-2 text-right whitespace-nowrap tabular-nums font-medium align-top ${line.teBepalen ? 'text-red-600' : 'text-td-navy'}`}>
                              {line.subtotal != null ? euro.format(line.subtotal) : ''}
                            </td>
                          </>
                        ) : (
                          <td colSpan={3} />
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function OpenPosTab() {
  const [data, setData] = useState<OpenPosResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNoPo, setShowNoPo] = useState(false);
  // Which PO is in edit mode (one at a time). null = none.
  const [editingPoId, setEditingPoId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchOpenPos());
    } catch {
      setError('Lopende inkooporders laden mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  // Exiting edit mode refetches the overview so the summary reflects the edits.
  const handleExitEdit = useCallback(() => {
    setEditingPoId(null);
    void load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-sm text-td-muted font-medium">
          {data ? `${data.pos.length} lopende inkoop${data.pos.length === 1 ? 'order' : 'orders'}` : ' '}
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-td-navy hover:bg-gray-50 disabled:opacity-50"
        >
          Vernieuwen
        </button>
      </div>

      {loading && !data ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Lopende inkooporders laden...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="text-sm font-semibold">{error}</p>
        </div>
      ) : !data || data.pos.length === 0 ? (
        <div className="bg-gray-50 text-td-muted p-6 rounded-xl text-center text-sm">
          Geen lopende inkooporders. Bestelbonnen worden aangemaakt vanuit het dagoverzicht.
        </div>
      ) : (
        <div className="space-y-3">
          {data.pos.map((po) => (
            <PoCard
              key={po.poId}
              po={po}
              editing={editingPoId === po.poId}
              onEnterEdit={() => setEditingPoId(po.poId)}
              onExitEdit={handleExitEdit}
            />
          ))}
        </div>
      )}

      {data && data.vendorsWithoutPo.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowNoPo((v) => !v)}
            className="text-sm text-td-muted hover:text-td-navy"
          >
            {showNoPo ? '▾' : '▸'} {data.vendorsWithoutPo.length} onderaannemer(s) zonder lopende PO
          </button>
          {showNoPo && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.vendorsWithoutPo.map((v) => (
                <span key={v.vendorId} className="text-xs bg-gray-100 text-td-muted px-2 py-1 rounded">
                  {v.vendorName}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
